import PropTypes from 'prop-types';
import document from 'global/document';
import { DEFAULT_CATCHUP_PLAYBACK_RATE, DEFAULT_SEEK_THRESHOLD_MULTIPLIER, DEFAULT_FALLBACK_PLAYBACK_RATE } from './constants.js';

const prefixes = [
  "webkit",
  "moz",
  "ms"
];
function getEventProperty(eventName) {
  return `on${eventName}`;
}
function prefixEventNameInDocument(standardEventName) {
  if (getEventProperty(standardEventName) in document) {
    return standardEventName;
  }
  return prefixes.find((prefix) => {
    const prefixedEventName = `${prefix}${standardEventName}`;
    return getEventProperty(prefixedEventName) in document;
  });
}
function getFullscreenChangeEventName() {
  const eventName = prefixEventNameInDocument("fullscreenchange");
  if (eventName && eventName.indexOf("ms") === 0) {
    return "MSFullscreenChange";
  }
  return eventName;
}
const keySystemConfigShape = PropTypes.shape({
  defaultLicenseUrl: PropTypes.string,
  getLicenseUrl: PropTypes.func,
  getLicenseHeaders: PropTypes.func,
  options: PropTypes.shape({})
});
const keySystemsConfigShape = PropTypes.shape({
  playready: keySystemConfigShape,
  widevine: keySystemConfigShape,
  fairplay: keySystemConfigShape
});
const fullscreenChangeEventName = getFullscreenChangeEventName();
function getPrefixedPropertyIn(element, standardProperty) {
  let foundProperty = standardProperty;
  if (!(standardProperty in element)) {
    const capitalizedProperty = `${standardProperty.slice(0, 1).toUpperCase()}${standardProperty.slice(1)}`;
    foundProperty = prefixes.find((prefix) => `${prefix}${capitalizedProperty}` in element);
  }
  let property = element[foundProperty];
  if (typeof property === "function") {
    property = property.bind(element);
  }
  return property;
}
function getPrefixedPropertyInDocument(standardProperty) {
  return getPrefixedPropertyIn(document, standardProperty);
}
function addFullscreenChangeEventListener(listener) {
  document.addEventListener(fullscreenChangeEventName, listener);
}
function removeFullscreenChangeEventListener(listener) {
  document.removeEventListener(fullscreenChangeEventName, listener);
}
const prefixedExitFullscreen = getPrefixedPropertyInDocument("exitFullscreen");
function exitFullscreen() {
  if (prefixedExitFullscreen) {
    return prefixedExitFullscreen();
  }
  if ("mozCancelFullScreen" in document) {
    return document.mozCancelFullScreen();
  }
  return Promise.reject(new Error("A function to exitFullscreen can not be found"));
}
function requestFullscreen(element, options) {
  const prefixedRequestFullscreen = getPrefixedPropertyIn(element, "requestFullscreen") || element.mozRequestFullScreen?.bind(element);
  if (prefixedRequestFullscreen)
    return prefixedRequestFullscreen(options);
  return Promise.reject(new Error("Fullscreen API is not supported"));
}
function getFullscreenElement() {
  const fullscreenElement = getPrefixedPropertyInDocument("fullscreenElement");
  return fullscreenElement || fullscreenElement === null ? fullscreenElement : document.mozFullScreenElement;
}
function getDefaultLowLatencyConfig(lowLatencyConfig = {}) {
  const {
    targetLatency,
    catchup: {
      playbackRate: catchupPlaybackRate = DEFAULT_CATCHUP_PLAYBACK_RATE,
      playbackRateThreshold: catchupPlaybackRateThreshold = 0,
      seekThreshold: catchupSeekThreshold = targetLatency * DEFAULT_SEEK_THRESHOLD_MULTIPLIER
    } = {},
    fallback: {
      playbackRate: fallbackPlaybackRate = DEFAULT_FALLBACK_PLAYBACK_RATE,
      playbackRateThreshold: fallbackPlaybackRateThreshold = 0,
      seekThreshold: fallbackSeekThreshold
    } = {}
  } = lowLatencyConfig;
  return {
    targetLatency,
    catchup: {
      playbackRate: catchupPlaybackRate,
      playbackRateThreshold: catchupPlaybackRateThreshold,
      seekThreshold: catchupSeekThreshold
    },
    fallback: {
      playbackRate: fallbackPlaybackRate,
      playbackRateThreshold: fallbackPlaybackRateThreshold,
      seekThreshold: fallbackSeekThreshold
    }
  };
}

export { addFullscreenChangeEventListener, exitFullscreen, getDefaultLowLatencyConfig, getFullscreenElement, getPrefixedPropertyIn, getPrefixedPropertyInDocument, keySystemsConfigShape, removeFullscreenChangeEventListener, requestFullscreen };
//# sourceMappingURL=utils.js.map
